/**
 * This file contains helper functions and variables that can generally be used
 * across all types of projections (full season, rest of season and weekly).
 * Each individual type of projection may also have its own helper file with
 * functions and variables specific to it.
 * 
 * As this file is being created, there are probably some things in the file
 * ProjectionHelper.js that could/should be moved here.
 */
import { HIGH, HIGH_BG_CLASS, LOW, LOW_BG_CLASS, MED, MED_BG_CLASS } from "./DsHelper.js";

/**
 * @param int $percentLow The `percent_low` value on either a Projection or
 * WeeklyPrediction record
 * @param int $fantasyPosition The `fantasy_position` of the player that the
 * `percent_low` value is for
 * @param {Array<object>} lowRangePoints Array with fantasy position as keys 
 * and value being an array that has two values for the low,high markers
 * @return string
 */
export const getPercentLowLevel = function (percentLow, fantasyPosition, lowRangePoints) {
    if (!lowRangePoints || !lowRangePoints[fantasyPosition]) return false;
    const rangePoints = lowRangePoints[fantasyPosition];
    return percentLow < rangePoints[0] ? HIGH : percentLow <= rangePoints[1] ? MED : LOW;
};

/**
 * @param int $percentHigh The `percent_high` value on either a Projection or
 * WeeklyPrediction record
 * @param int $fantasyPosition The `fantasy_position` of the player that the
 * `percent_low` value is for
 * @param {Array<object>} highRangePoints Array with fantasy position as keys 
 * and value being an array that has two values for the low,high markers
 * @return string
 */
export const getPercentHighLevel = function (percentHigh, fantasyPosition, highRangePoints) {
    if (!highRangePoints || !highRangePoints[fantasyPosition]) return false;
    let rangePoints = highRangePoints[fantasyPosition];
    return percentHigh < rangePoints[0] ? LOW : percentHigh <= rangePoints[1] ? MED : HIGH;
};

/**
 * Returns a string representing the 'level' the player falls into for their
 * `percent_low` or `percent_high`
 * @param float $percentVar String representation of whether we are using
 * `percent_low` or `percent_high` that uses the values self::HIGH of
 * self::LOW out of convenience even though they related to either of those vars
 * @param string $percentValue
 * @param string $fantasyPosition
 * @param array $lowRangePoints Array with fantasy position as keys and value
 * being an array that has two values for the low,high markers
 * @param array $highRangePoints Array with fantasy position as keys and value
 * being an array that has two values for the low,high markers
 * @return string
 */
export const getLevelText = function (percentVar, percentValue, fantasyPosition, lowRangePoints, highRangePoints) {
    let percentLevel = false;

    if (percentVar == LOW) {
        percentLevel = getPercentLowLevel(percentValue, fantasyPosition, lowRangePoints);
    }
    if (percentVar == HIGH) {
        percentLevel = getPercentHighLevel(percentValue, fantasyPosition, highRangePoints);
    }
    switch (percentLevel) {
        case LOW:
            return 'Low';
        case MED:
            return 'Med';
        case HIGH:
            return 'High';
    }
    return false;
};

/**
 * Returns a string representing the 'level' the player falls into for their
 * `percent_low` projection value
 * @param float $percentLow
 * @param string $fantasyPosition
 * @param array $lowRangePoints Array with fantasy position as keys and value
 * being an array that has two values for the low,high markers
 * @return string
 */
export const getPercentLowLevelText = function (percentLow, fantasyPosition, lowRangePoints) {
    return getLevelText(LOW, percentLow, fantasyPosition, lowRangePoints);
};

/**
 * Returns a string representing the 'level' the player falls into for their
 * `percent_high` projection value
 * @param float $percentHigh
 * @param string $fantasyPosition
 * @param array $highRangePoints Array with fantasy position as keys and value
 * being an array that has two values for the low,high markers
 * @return string
 */
export const getPercentHighLevelText = function (percentHigh, fantasyPosition, highRangePoints) {
    return getLevelText(HIGH, percentHigh, fantasyPosition, null, highRangePoints);
};

/**
 * Calculates floor points from a projection or weekly prediction using
 * `percent_low` and fantasy points.
 *
 * Only `eligiblePts` are scaled by percent_low. `excludedPts` are added back
 * at face value (e.g. bonuses, conditional IDP).
 *
 * @param {number} eligiblePts Points eligible for floor scaling
 * @param {number} percentLow
 * @param {number} [excludedPts=0] Points excluded from floor scaling
 * @return {number}
 */
export const getFloorPoints = function (eligiblePts, percentLow, excludedPts = 0) {
    const eligible = parseFloat(eligiblePts) || 0;
    const excluded = parseFloat(excludedPts) || 0;
    const percent = parseFloat(percentLow) || 0;
    return parseFloat((eligible - eligible * (percent / 100) + excluded).toFixed(1));
};

/**
 * Calculates ceiling points from a projection or weekly prediction using
 * `percent_high` and fantasy points.
 *
 * Only `eligiblePts` are scaled by percent_high. `excludedPts` are added back
 * at face value (e.g. bonuses, conditional IDP).
 *
 * @param {number} eligiblePts Points eligible for ceiling scaling
 * @param {number} percentHigh
 * @param {number} [excludedPts=0] Points excluded from ceiling scaling
 * @return {number}
 */
export const getCeilingPoints = function (eligiblePts, percentHigh, excludedPts = 0) {
    const eligible = parseFloat(eligiblePts) || 0;
    const excluded = parseFloat(excludedPts) || 0;
    const percent = parseFloat(percentHigh) || 0;
    return parseFloat((eligible + eligible * (percent / 100) + excluded).toFixed(1));
};

/**
 * Constant values for the `percent_high_odds` column in `weekly_prediction` 
 * and `projection` tables
 */
export const PERCENT_HIGH_ODDS_LOW = 0;
export const PERCENT_HIGH_ODDS_MEDIUM = 1;
export const PERCENT_HIGH_ODDS_HIGH = 2;

/**
 * List of options for percent high odds with readable labels
 * @var array
 */
export const PERCENT_HIGH_ODDS_OPTIONS = {
    [PERCENT_HIGH_ODDS_LOW]: 'Low',
    [PERCENT_HIGH_ODDS_MEDIUM]: 'Medium',
    [PERCENT_HIGH_ODDS_HIGH]: 'High',
};

/**
 * Returns a clalss used for 'percent_high_odds`
 * @param {Number} value `percent_high_odds` from `projection` records
 * @returns {string}
 */
export const getPercentHighOddsClass = function (value) {
    switch (value) {
        case PERCENT_HIGH_ODDS_LOW:
            return LOW_BG_CLASS;
        case PERCENT_HIGH_ODDS_MEDIUM:
            return MED_BG_CLASS;
        case PERCENT_HIGH_ODDS_HIGH:
            return HIGH_BG_CLASS
    }
}

/**
 * Returns the readable label for this value
 * @param int percentHighOdds
 * @return string
 */
export const getPercentHighOddsLabel = function (percentHighOdds) {
    return PERCENT_HIGH_ODDS_OPTIONS[percentHighOdds];
}

/**
 * Gets the css class used on elements based on floor pts
 * @param {Number} percentLow `percent_low` from a projection-type record
 * @param {Number} fantasyPosition `fantasy_position` from `player` records
 * @param {Array<object>} lowRangePts Array with fantasy position as keys 
 * and value being an array that has two values for the low,high markers
 * @returns {string}
 */
export const getFloorPtsClass = function (percentLow, fantasyPosition, lowRangePts) {
    const percentLowLevel = getPercentLowLevel(percentLow, fantasyPosition, lowRangePts);
    if (percentLowLevel == LOW) {
        return LOW_BG_CLASS;
    }
    if (percentLowLevel == MED) {
        return MED_BG_CLASS;
    }
    return HIGH_BG_CLASS
}

/**
 * Returns HTML markup for a pill containing the floor points with a class for
 * proper highlighting based on the risk level.
 * @param {number} floorPts 
 * @param {number} percentHigh 
 * @param {string} fantasyPosition
 * @param {Array<object>} lowRangePts Array with fantasy position as keys 
 * and value being an array that has two values for the low,high markers
 * @param {number} [toFixed] Number of decimal places to display using `toFixed`
 * @returns 
 */
export const getFloorPtsPill = (floorPts, percentLow, fantasyPosition, lowRangePts, toFixed = 1) => {
    const cssClass = getFloorPtsClass(percentLow, fantasyPosition, lowRangePts);
    return '<span class="ds-pill ' + cssClass + '">' + floorPts.toFixed(toFixed) + '</span>';
}

/**
 * Gets the css class used on elements based on floor pts
 * @param {Number} percentHigh `percent_high` from a projection-type record
 * @param {Number} fantasyPosition `fantasy_position` from `player` records
 * @param {Array<object>} highRangePoints Array with fantasy position as keys 
 * and value being an array that has two values for the low,high markers
 * @returns {string}
 */
export const getCeilingPtsClass = function (percentHigh, fantasyPosition, highRangePoints) {
    const percentHighLevel = getPercentHighLevel(percentHigh, fantasyPosition, highRangePoints);
    if (percentHighLevel == LOW) {
        return LOW_BG_CLASS;
    }
    if (percentHighLevel == MED) {
        return MED_BG_CLASS;
    }
    return HIGH_BG_CLASS
}

/**
 * Returns HTML markup for a pill containing the floor points with a class for
 * proper highlighting based on the risk level.
 * @param {number} ceilingPts 
 * @param {number} percentHigh 
 * @param {string} fantasyPosition
 * @param {Array<object>} highRangePoints Array with fantasy position as keys 
 * and value being an array that has two values for the low,high markers
 * @param {number} [toFixed] Number of decimal places to display using `toFixed`
 * @returns 
 */
export const getCeilingPtsPill = (ceilingPts, percentHigh, fantasyPosition, highRangePoints, toFixed = 1) => {
    const cssClass = getCeilingPtsClass(percentHigh, fantasyPosition, highRangePoints);
    return '<span class="ds-pill ' + cssClass + '">' + ceilingPts.toFixed(toFixed) + '</span>';
}

/**
 * Returns an adjusted value based on a selected strategy by the user that leans
 * the DS 3D projection further towards the floor or ceiling base on the user's
 * selection. This is shared in the LCC Team Dashboard and the Who to Start tool.
 * @param {number} startingValue The starting points value before adjustments
 * @param {number} floorPts The floor points value
 * @param {number} ceilingPts The ceiling points value
 * @param {number} strategyId The user selected 
 * @returns {number}
 */
export const getAdjustmentForStrategy = ( startingValue, floorPts, ceilingPts, strategyId) => {
    if(strategyId == 3){
        return startingValue; // --- 3 strategy is neutral and uses DS value
    }
    // --- Since 3 is "neutral" for each step away from 3 we modify the
    // --- increase weight of weekly/floor by .25 and reduce weekly by .25
    const ceilingOrFloorMultiplier = Math.abs(3 - strategyId) * .25;
    const startingValueMultiplier = 1 - ceilingOrFloorMultiplier;

    // --- The points we weight against weekly ones is floor pts if the
    // --- strategyId is 1 or 2 and ceiling pts if its 3 or 4.
    const ptsToWeightAgainst3d = [1, 2].includes(strategyId)
        ? floorPts
        : ceilingPts;

    // --- Apply the weights to the 3d and floor/ceiling
    return (startingValue * startingValueMultiplier) + (ptsToWeightAgainst3d * ceilingOrFloorMultiplier);
}

/**
 * @type {string} Constant for "general" category stats that usually apply
 * to every player
 */
export const STAT_CATEGORY_GENERAL = 'general';

/**
 * @type {string} Constant for "passing" category stats that usually apply
 * to RBs, WRs and TEs
 */
export const STAT_CATEGORY_PASSING = 'passing';

/**
 * @type {string} Constant for "rushing" category stats that usually apply
 * to RBs, QBs, and sometimes WRs
 */
export const STAT_CATEGORY_RUSHING = 'rushing';

/**
 * @type {string} Constant for "receiving" category stats that usually apply
 * to RBs, WRs and TEs
 */
export const STAT_CATEGORY_RECEIVING = 'receiving';

/**
 * @type {string} Constant for "return" category stats that usually apply
 * to RBs, WRs and TEs
 */
export const STAT_CATEGORY_RETURN = 'return';

/**
 * @type {string} Constant for "extra" category stats that usually apply
 * to every player
 */
export const STAT_CATEGORY_EXTRA = 'extra';

/**
 * @type {string} Constant for "kicking" category stats that usually apply
 * to kickers
 */
export const STAT_CATEGORY_KICKING = 'kicking';

/**
 * @type {string} Constant for "def" category stats that usually apply
 * to team defense
 */
export const STAT_CATEGORY_TEAM_DEFENSE = 'def';

/**
 * @type {string} Constant for "idp" category stats that usually apply
 * to DL, DB, LB
 */
export const STAT_CATEGORY_IDP = 'idp';

/**
 * @type {string} Constant for "coach" category stats that usually apply
 * to coaches
 */
export const STAT_CATEGORY_COACH = 'coach';

/**
 * @type {string} Constant for "detail" category that is for columns like
 * `create_time`, `fantasy_pts` and other data.
 */
export const STAT_CATEGORY_DETAIL = 'detail';

/**
 * @type {string} Constant for "offBonus" category that is for columns like
 * `create_time`, `fantasy_pts` and other data.
 */
export const STAT_CATEGORY_OFFENSIVE_BONUS = 'offBonus';

/**
 * @type {array} Array of stat categories
 */
export const STAT_CATEGORY_DEFINITIONS = [
    { id: STAT_CATEGORY_GENERAL, label: 'General' },
    { id: STAT_CATEGORY_PASSING, label: 'Passing' },
    { id: STAT_CATEGORY_RUSHING, label: 'Rushing' },
    { id: STAT_CATEGORY_RECEIVING, label: 'Receiving' },
    { id: STAT_CATEGORY_OFFENSIVE_BONUS, label: 'Offensive Bonus' },
    { id: STAT_CATEGORY_RETURN, label: 'Return' },
    { id: STAT_CATEGORY_EXTRA, label: 'Extra' },
    { id: STAT_CATEGORY_KICKING, label: 'Kicking' },
    { id: STAT_CATEGORY_TEAM_DEFENSE, label: 'Team Defense' },
    { id: STAT_CATEGORY_IDP, label: 'IDP' },
    { id: STAT_CATEGORY_COACH, label: 'Coach' },
    { id: STAT_CATEGORY_DETAIL, label: 'Detail' },
];

